<template>
  <div>
    <div>
      <div>
        <div class="form-group email-noti-container">
          <div class="email-noti-label">
            <label>Email Address: </label>
          </div>
          <div class="d-flex align-items-center webhook-input">
            <b-form-input
              type="text"
              class="form-control"
              v-model.trim="$v.localValue.addresses.$model"
              :state="validateState('addresses')"
              placeholder="email@domain.com"
            />

            <v-btn icon @click.prevent="submit">
              <v-icon size="30" color="green darken-2">fa fa-save</v-icon>
            </v-btn>

            <v-btn icon @click.prevent="removeAllEmails">
              <v-icon size="30" color="red darken-2">fa fa-trash</v-icon>
            </v-btn>

            <div v-if="!$v.localValue.addresses.required">
              <p style="color:red;">A valid email address is required</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      class="webhook-confirmation"
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import { required, email, helpers } from "vuelidate/lib/validators";
import MerchantSettingsPermissionsMixin from "@/components/merchantsettings/MerchantSettingsPermissionsMixin.js";
import { API, graphqlOperation } from "aws-amplify";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";

const multipleEmail = value => {
  var emailsList = value.split(",");

  const emailValidation = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  for (var email = 0; email < emailsList.length; email++) {
    if (!emailValidation.test(emailsList[email])) {
      return false;
    }
  }
  return true;
};
const AlertModal = AlertModalMixin.AlertModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [MerchantSettingsPermissionsMixin, AlertModalMixin],
  props: {
    event: {
      SK: {
        type: String,
        required: true
      },
      Name: {
        type: String,
        required: true
      },
      Description: {
        type: String,
        required: true
      },
      Example: {
        type: Object,
        required: false,
        default() {
          return {};
        }
      }
    },
    value: {
      addresses: {
        type: String,
        required: true,
        default: ""
      }
    }
  },
  data() {
    return {};
  },
  validations: {
    localValue: {
      addresses: {
        required,
        multipleEmail
      }
    }
  },
  watch: {
    localValue: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      }
    },
    eventDisplayName() {
      return this.event.Name;
    },
    eventName() {
      return this.event.SK;
    }
  },
  methods: {
    emailListContent(emailList) {
      return emailList.join(",");
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.localValue[name];
      return $dirty ? !$error : null;
    },
    async saveEmailNotificationAddresses() {
      try {
        const saveEmailNotificationAddresses = `
                mutation saveEmailNotificationAddresses(
                  $emailaddresses: SaveEmailNotificationAddressesInput!
                ) {
                    saveEmailNotificationAddresses(emailaddresses: $emailaddresses) {
                      event
                      emailaddresses
                    }
                }
              `;
        this.$v.$touch();
        if (this.$v.$invalid) {
          throw new Error("An email entered is invalid");
        }

        const emailaddresses = {
          event: this.eventName,
          emailaddresses: this.localValue.addresses.split(",")
        };

        const saveEmailNotificationAddressesResponse = await API.graphql({
          ...graphqlOperation(saveEmailNotificationAddresses, {
            emailaddresses
          })
        });

        return saveEmailNotificationAddressesResponse.data
          .saveEmailNotificationAddresses;
      } catch (e) {
        this.$logger.error(e);
        throw new Error("Error saving new email address");
      }
    },
    async removeEmailNotificationAddresses() {
      try {
        const removeEmailNotificationAddresses = `
                mutation saveEmailNotificationAddresses(
                  $emailaddresses: SaveEmailNotificationAddressesInput!
                ) {
                    saveEmailNotificationAddresses(emailaddresses: $emailaddresses) {
                      event
                      emailaddresses
                    }
                }
              `;

        const emailaddresses = {
          event: this.eventName,
          emailaddresses: []
        };

        const removeEmailNotificationAddressesResponse = await API.graphql({
          ...graphqlOperation(removeEmailNotificationAddresses, {
            emailaddresses
          })
        });

        return removeEmailNotificationAddressesResponse.data
          .saveEmailNotificationAddresses;
      } catch (e) {
        this.$logger.error(e);
        throw new Error("Error removing new email address");
      }
    },

    async submit() {
      this.$v.$touch();
      if (!this.hasEditMerchantSettingsPermission || this.$v.$invalid) return;
      const confirmationTitle = `Save ${this.eventDisplayName} Email Address?`;
      const confirmationMessage = `Are you sure you want to save this email address?`;
      const confirm = await this.confirm(
        confirmationTitle,
        confirmationMessage,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      let saveEmailAddressResponseMessage = "";
      let alertType = AlertModal.ERROR;
      this.startLoadingDialog(`Saving ${this.eventDisplayName} Email Address`);
      try {
        const emailNotificationAddressesConfig = await this.saveEmailNotificationAddresses();

        if (!emailNotificationAddressesConfig) {
          saveEmailAddressResponseMessage = `There was an error saving the ${this.eventDisplayName} Email Address`;
          alertType = AlertModal.ERROR;
        } else {
          this.localValue.addresses = this.emailListContent(
            emailNotificationAddressesConfig.emailaddresses
          );

          saveEmailAddressResponseMessage = `The ${this.eventDisplayName} has been saved`;
          alertType = AlertModal.SUCCESS;
        }
      } catch (e) {
        this.$logger.error(
          `Error saving ${this.eventDisplayName} Email Address`
        );
        this.$logger.error(e);
        saveEmailAddressResponseMessage = `An error occurred while saving ${this.eventDisplayName} Email Address`;
        alertType = AlertModal.ERROR;
      }

      this.stopLoadingDialog(saveEmailAddressResponseMessage, alertType);
    },

    async removeAllEmails() {
      if (!this.hasEditMerchantSettingsPermission) return;
      const confirmationTitle = `Remove all ${this.eventDisplayName} Email Addresses?`;
      const confirmationMessage = `Are you sure you want to remove all email addresses?`;
      const confirm = await this.confirm(
        confirmationTitle,
        confirmationMessage,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      let removeEmailAddressResponseMessage = "";
      let alertType = AlertModal.ERROR;
      this.startLoadingDialog(
        `Removing all ${this.eventDisplayName} Email Addresses`
      );
      try {
        const emailNotificationAddressesConfig = await this.removeEmailNotificationAddresses();

        if (!emailNotificationAddressesConfig) {
          removeEmailAddressResponseMessage = `There was an error removing all ${this.eventDisplayName} Email Addresses`;
          alertType = AlertModal.ERROR;
        } else {
          this.localValue.addresses = "";

          removeEmailAddressResponseMessage = `The ${this.eventDisplayName} has been removed`;
          alertType = AlertModal.SUCCESS;
        }
      } catch (e) {
        this.$logger.error(
          `Error removing ${this.eventDisplayName} Email Addresses`
        );
        this.$logger.error(e);
        removeEmailAddressResponseMessage = `An error occurred while removing ${this.eventDisplayName} Email Addresses`;
        alertType = AlertModal.ERROR;
      }

      this.stopLoadingDialog(removeEmailAddressResponseMessage, alertType);
    }
  }
};
</script>

<style lang="scss" scoped>
.email-noti-container {
  display: flex;
  flex-direction: column;
}

.email-noti-label label {
  font-weight: 500 !important;
  font-size: 14px !important;
  margin-bottom: 1rem;
}
</style>
